import {NgModule} from '@angular/core';
import {RouterModule, RouterStateSnapshot, Routes} from '@angular/router';
import {environment} from '../environments/environment';
import {CandidateProfilePageComponent} from './client/candidate-profile-page/candidate-profile-page.component';
import {HomePageComponent} from './client/home-page/home-page.component';
import {InvoicesPageComponent} from './client/invoices-page/invoices-page.component';
import {PoolsPageComponent} from './client/pools-page/pools-page.component';
import {PositionCandidatesPageComponent} from './client/position-candidates-page/position-candidates-page.component';
import {PositionDetailsPageComponent} from './client/position-details-page/position-details-page.component';
import {PositionsPageDecoyComponent} from './client/positions-page-decoy/positions-page-decoy.component';
import {PositionsPageComponent} from './client/positions-page/positions-page.component';
import {QccGuard} from './client/qcc.guard';
import {ReportsPageComponent} from './client/reports-page/reports-page.component';
import {TimecardDetailsPageComponent} from './client/timecard-details-page/timecard-details-page.component';
import {TimecardsPageComponent} from './client/timecards-page/timecards-page.component';
import {AccountForgotPageComponent} from './core/account-forgot-page/account-forgot-page.component';
import {AccountResetPageComponent} from './core/account-reset-page/account-reset-page.component';
import {ErrorPageComponent} from './core/error-page/error-page.component';
import {LoginAsPageComponent} from './core/login-as-page/login-as-page.component';
import {LoginPageComponent} from './core/login-page/login-page.component';
import {LogoutPageComponent} from './core/logout-page/logout-page.component';
import {NotFoundPageComponent} from './core/not-found-page/not-found-page.component';
import {RegisterCompleteComponent} from './core/register-complete/register-complete.component';
import {RegisterConfirmComponent} from './core/register-confirm/register-confirm.component';
import {RequestPageComponent} from './core/request-page/request-page.component';
import {TermsPageComponent} from './core/terms-page/terms-page.component';
import {UserAccountInformationComponent} from './core/user-account-information/user-account-information.component';
import {UserAccountSettingsComponent} from './core/user-account-settings/user-account-settings.component';
import {TalentProfilePageComponent} from './profile/talent-profile-page/talent-profile-page.component';
import {AuthRequiredGuard} from './shared/auth-required.guard';
import {AuthGuard} from './shared/auth.guard';
import {RedirectGuard} from './shared/redirect.guard';
import {DiversityInsightComponent} from './client/diversity-insight/diversity-insight.component';

export var determineCandidateProfilePageComponentCategory = (s: RouterStateSnapshot) =>
  s.root.queryParams['returnTo'] === 'pools' ? 'pools' : 'positions';

export const routes: Routes = [
  {path: '', redirectTo: 'home', pathMatch: 'full'},

  // CoreModule routes
  {path: 'error', component: ErrorPageComponent},
  {path: 'login', component: LoginPageComponent, canActivate: [AuthGuard]},
  {path: 'login/as', component: LoginAsPageComponent},
  {
    path: 'account/forgot',
    component: AccountForgotPageComponent,
    data: environment.flags.matLogin ? {
      redirectUrl: `${environment.matHost}/myaquent?PROC=AWUIDrawForgotUsernamePassword`
    } : {},
    canActivate: [RedirectGuard]
  },
  {path: 'account/reset', component: AccountResetPageComponent},
  {path: 'account/settings', component: UserAccountSettingsComponent, canActivate: [AuthRequiredGuard]},
  {path: 'account/information', component: UserAccountInformationComponent, canActivate: [AuthRequiredGuard]},
  {path: 'register/complete', component: RegisterCompleteComponent},
  {path: 'register/confirm', component: RegisterConfirmComponent},
  {path: 'logout', component: LogoutPageComponent, canActivate: [AuthGuard]},
  {path: 'terms', component: TermsPageComponent, canActivate: [AuthGuard]},

  // QccModule routes
  {path: 'qcc', loadChildren: 'app/qcc/qcc.module#QccModule'},

  // ClientModule routes TODO lazy loading
  {
    path: 'home',
    component: HomePageComponent,
    canActivate: [AuthRequiredGuard]
  },
  {
    path: 'qcc/:id',
    component: PositionsPageComponent,
    canActivate: [AuthGuard, QccGuard]
  },
  {
    path: 'positions',
    redirectTo: 'positions/open',
    pathMatch: 'full'
  },
  {
    path: 'positions/:status',
    component: PositionsPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'positions'}
  },
  {
    path: 'positions/:status/qcc',
    component: PositionsPageDecoyComponent,
    canActivate: [AuthGuard, QccGuard],
    data: {category: 'positions'}
  },
  {
    path: 'positions/:id/candidates',
    component: PositionCandidatesPageComponent,
    canActivate: [AuthGuard],
    data: {category: 'positions', tease: true}
  },
  {
    path: 'positions/:id/candidates/:candidate',
    component: CandidateProfilePageComponent,
    canActivate: [AuthGuard],
    data: {
      category: determineCandidateProfilePageComponentCategory,
      tease: true
    }
  },
  {
    path: 'positions/:id/details',
    component: PositionDetailsPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'positions'}
  },
  {
    path: 'pools',
    redirectTo: 'pools/all',
    pathMatch: 'full'
  },
  {
    path: 'pools/:view',
    component: PoolsPageComponent,
    canActivate: [AuthGuard],
    data: {category: 'pools', tease: true}
  },
  {
    path: 'timecards',
    redirectTo: 'timecards/unsigned',
    pathMatch: 'full'
  },
  {
    path: 'timecards/:view',
    component: TimecardsPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'timecards'}
  },
  {
    path: 'timecards/:id/details',
    component: TimecardDetailsPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'timecards'}
  },
  {
    path: 'invoices',
    redirectTo: 'invoices/statement',
    pathMatch: 'full'
  },
  {
    path: 'invoices/:view',
    component: InvoicesPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'invoices'}
  },
  {
    path: 'reports',
    component: ReportsPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'reports'}
  },
  {
    path: 'request',
    component: RequestPageComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'request'}
  },
  {
    path: 'diversity',
    redirectTo: 'diversity/gender',
    pathMatch: 'full'
  },
  {
    path: 'diversity/:view',
    component: DiversityInsightComponent,
    canActivate: [AuthRequiredGuard],
    data: {category: 'diversity'}
  },

  // ProfileModule TODO lazy load
  {
    path: 'profile/:talentId',
    component: TalentProfilePageComponent,
    data: {tease: true}
  },
  {path: 'submittal/:submittalId', component: TalentProfilePageComponent},

  // Legacy redirects
  {path: 'dashboard', redirectTo: 'home', pathMatch: 'full'},
  {path: 'position/:id', redirectTo: 'positions/:id/candidates'},
  {path: 'position/:id/candidate/:candidate', redirectTo: 'positions/:id/candidates/:candidate'},

  // Lazy loaded modules
  {path: 'demo', loadChildren: 'app/demo/demo.module#DemoModule'},
  {path: 'review', loadChildren: 'app/reviews/reviews.module#ReviewsModule'},
  {path: 'feedback', loadChildren: 'app/feedback/feedback.module#FeedbackModule'},
  {path: 'instant-job-alert/email/cancellation', loadChildren: 'app/instant-job-alert/instant-job-alert.module#InstantJobAlertModule'},
  // 404
  {path: '**', component: NotFoundPageComponent}
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class ClientRoutingModule {
}
